import {
    Box,
    IconButton,
    InputAdornment,
    InputBaseComponentProps,
    TextField,
    Tooltip,
    type TooltipProps,
} from '@mui/material';
import React, { ChangeEvent, ReactNode, useEffect, useId, useRef } from 'react';
import { useIntl } from 'react-intl';
import { StepResolver, computeSteppedValue } from './numberInputStepping';
import { decimalEntryRefusalMessage, useDecimalEntry } from './useDecimalEntry';

interface NumberInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    /**
     * Called when the field's validity changes. The field never commits an
     * unparseable or out-of-range value through {@link onChange}, so a
     * consumer that must not act on a stale committed number while the box
     * shows something else gates on this instead.
     */
    onValidityChange?: (isValid: boolean) => void;
    /**
     * Stepper increment. Either a constant, or an adaptive function of the
     * current value (e.g. finer steps near small margins). Note that typed
     * input is validated against `precision`, not `step`.
     */
    step?: StepResolver;
    /**
     * When true, the increment/decrement arrows snap the result to the step
     * grid (anchored at `min`, or 0 when unbounded) instead of adding the raw
     * step. This keeps off-grid typed values from drifting off the grid:
     * 0.22 steps to 0.25 / 0.20 rather than 0.27 / 0.17. Typed values are not
     * snapped — only the arrows.
     */
    snapToStep?: boolean;
    min?: number;
    max?: number;
    precision?: number;
    placeholder?: string;
    disabled?: boolean;
    inputProps?: InputBaseComponentProps;
    fullWidth?: boolean;
    label?: ReactNode;
    autoFocus?: boolean;
    error?: string | null;
    /**
     * If true, fires onChange on every keystroke that parses to a valid
     * in-range number. If false (default), onChange fires only on blur or
     * Enter — useful when intermediate edits shouldn't drive downstream
     * work, but a poor fit for cases where the input is the sole control
     * driving a live preview (the user shouldn't have to blur to see the
     * effect of their edit).
     *
     * The start of an entry is committed on its way to being typed (`1` of
     * `1.000,5`, `12` of `12.7`). While the field then shows an entry it
     * does not accept, and when it is left that way, onChange receives the
     * value the field had when the edit began, so no part of a refused entry
     * stays committed.
     */
    commitOnChange?: boolean;
    /**
     * If true, the mouse wheel over the input steps the value exactly
     * like the arrow buttons (same `step`/`snapToStep`/min/max/precision
     * path), scrolling up to increment. Opt-in: capturing the wheel
     * preventDefaults page scrolling over the input, which would
     * surprise consumers whose inputs sit in scrollable lists.
     */
    stepOnWheel?: boolean;
    /**
     * Which way the range hint opens. It shows while the field is being
     * edited, and what sits under a numeric field is usually the slider
     * driving the same value.
     */
    tooltipPlacement?: TooltipProps['placement'];
    'data-testid'?: string;
}

const roundToPrecision = (num: number, precision: number = 10) => {
    const factor = Math.pow(10, precision);
    return Math.round(num * factor) / factor;
};

/**
 * A number field with stepper arrows. A typed entry is read by
 * {@link useDecimalEntry}: a point or a comma is the decimal separator, an
 * edit bringing a character no number is written with is not entered, and an
 * entry the field does not accept (no number, too many decimals, out of
 * range, or ambiguous such as `1,500`) is kept, marked, with the reason as
 * the field's accessible description, until the field is left. A step is not
 * part of an edit: it ends any, and the next keystroke starts a new one from
 * the stepped value.
 */
const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    onValidityChange,
    step = 1,
    snapToStep = false,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    precision = 2,
    placeholder = '',
    disabled = false,
    inputProps,
    label,
    fullWidth = false,
    autoFocus = false,
    error,
    commitOnChange = false,
    stepOnWheel = false,
    tooltipPlacement = 'top-start',
    'data-testid': dataTestId,
}) => {
    const intl = useIntl();
    const messageId = useId();
    const entry = useDecimalEntry({
        value,
        onChange,
        min,
        max,
        maxFractionDigits: precision,
        format: (shown) => (shown === null ? '' : shown.toFixed(precision)),
        applyWhileTyping: commitOnChange,
        refuseNonEntryCharacters: true,
    });

    const onValidityChangeRef = useRef(onValidityChange);
    onValidityChangeRef.current = onValidityChange;
    useEffect(() => {
        onValidityChangeRef.current?.(!entry.isInvalid);
    }, [entry.isInvalid]);

    /** Clamp a number to [min, max] and round to the input's precision. */
    const clamp = (v: number) =>
        Math.max(Math.min(roundToPrecision(v, precision), max), min);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) =>
        entry.onTextChange(event.target.value);

    // Grid origin the snap lattice is measured from: the lower bound when
    // finite, otherwise 0 (an unbounded input has no natural anchor).
    const stepAnchor = Number.isFinite(min) ? min : 0;

    const handleStep = (direction: 1 | -1) => {
        if (disabled) {
            return;
        }
        const base = value ?? stepAnchor;
        entry.apply(
            clamp(
                computeSteppedValue(
                    base,
                    direction,
                    step,
                    snapToStep,
                    stepAnchor
                )
            )
        );
    };

    const handleIncrement = () => handleStep(1);

    const handleDecrement = () => handleStep(-1);

    // Wheel stepping (opt-in): a NATIVE non-passive listener, because
    // React registers wheel handlers passively — preventDefault from a
    // React onWheel is ignored and the page would scroll under the
    // gesture. The handler is read through a ref so the listener is not
    // re-bound on every value change.
    const handleStepRef = useRef(handleStep);
    handleStepRef.current = handleStep;
    const wheelHostRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!stepOnWheel || disabled) {
            return;
        }
        const host = wheelHostRef.current;
        if (!host) {
            return;
        }
        const onWheel = (event: WheelEvent) => {
            if (event.deltaY === 0) {
                return;
            }
            event.preventDefault();
            handleStepRef.current(event.deltaY < 0 ? 1 : -1);
        };
        host.addEventListener('wheel', onWheel, { passive: false });
        return () => host.removeEventListener('wheel', onWheel);
    }, [stepOnWheel, disabled]);

    // Enter leaves the field: the blur commits the edit, and the cursor goes
    // with it so later keystrokes cannot land in a value already entered. An
    // entry the field does not accept keeps the field, marked, rather than
    // closing onto the value from before as if it were accepted.
    const inputRef = useRef<HTMLInputElement>(null);
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (event.key === 'Enter' && !entry.isInvalid) {
            inputRef.current?.blur();
        }
    };

    /** The range the field takes, as the hint shown while it is edited. */
    const rangeHint = (): string => {
        const parts: string[] = [];
        if (min > Number.NEGATIVE_INFINITY) {
            parts.push(intl.formatMessage({ id: 'numberInput.min' }, { min }));
        }
        if (max < Number.POSITIVE_INFINITY) {
            parts.push(intl.formatMessage({ id: 'numberInput.max' }, { max }));
        }
        return parts.join(', ');
    };

    /**
     * What the field tells the user while it is edited: the entry it did not
     * take, why it does not accept the entry it shows, or else its range,
     * which also says why an out-of-range entry is refused.
     */
    const shownMessage = (): string => {
        const { refusal } = entry;
        if (refusal !== null && refusal.kind !== 'outOfRange') {
            const { id, values } = decimalEntryRefusalMessage(refusal, {
                min,
                max,
                maxFractionDigits: precision,
            });
            return intl.formatMessage({ id }, values);
        }
        return entry.isTouched ? rangeHint() : '';
    };
    const message = shownMessage();

    return (
        <Tooltip
            id={messageId}
            open={message !== ''}
            placement={tooltipPlacement}
            title={message}
            disableHoverListener
            PopperProps={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -12],
                        },
                    },
                ],
            }}
        >
            <Box
                ref={wheelHostRef}
                display="flex"
                alignItems="center"
                data-testid={dataTestId}
            >
                <TextField
                    variant="outlined"
                    size="small"
                    fullWidth={fullWidth}
                    value={entry.text}
                    label={label}
                    inputRef={inputRef}
                    onFocus={entry.onFocus}
                    onChange={handleInputChange}
                    onBlur={entry.onBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={disabled}
                    error={Boolean(error || entry.isInvalid)}
                    helperText={error}
                    inputProps={{
                        inputMode: 'decimal',
                        'aria-describedby':
                            message !== '' ? messageId : undefined,
                        ...inputProps,
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Box display="grid">
                                    <IconButton
                                        size="small"
                                        onClick={handleIncrement}
                                        disabled={disabled}
                                        aria-label={intl.formatMessage({
                                            id: 'increaseValue',
                                        })}
                                        sx={{
                                            fontSize: 8,
                                            padding: '1px',
                                        }}
                                    >
                                        &#9650;
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={handleDecrement}
                                        disabled={disabled}
                                        aria-label={intl.formatMessage({
                                            id: 'decreaseValue',
                                        })}
                                        sx={{
                                            fontSize: 8,
                                            padding: '1px',
                                        }}
                                    >
                                        &#9660;
                                    </IconButton>
                                </Box>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        </Tooltip>
    );
};

export default NumberInput;
